import {
    ReceptionScheduleSlotModal,
    type ReceptionScheduleSlotModalRef,
} from '@src/pages/Services/components/ReceptionScheduleSlotModal';
import { ReceptionSchedulesSection } from '@src/pages/Services/components/ReceptionSchedulesSection';

import type { ServiceVmRaw } from '@shared/types/process';

import { useCallback, useEffect, useRef } from 'react';

type ReceptionScheduleDetailsWebComponentProps = {
    service?: Pick<ServiceVmRaw, 'id'>;
    hideTimer?: boolean;
};

export const ReceptionScheduleDetailsWebComponent = ({
    service,
    hideTimer,
}: ReceptionScheduleDetailsWebComponentProps) => {
    const refDiv = useRef<HTMLDivElement>(null);
    const receptionScheduleRef = useRef<ReceptionScheduleSlotModalRef>(null);

    useEffect(() => {
        const root = refDiv.current?.getRootNode();
        const host = (
            root instanceof ShadowRoot
                ? root.host
                : refDiv.current?.closest('reception-schedule-details')
        ) as
            | (HTMLElement & {
                  openReceptionSchedule?: (
                      buttonId: string,
                      organizationId?: string,
                  ) => void;
              })
            | null;

        if (!host) return;

        host.openReceptionSchedule = (buttonId, organizationId) => {
            if (!buttonId?.trim()) return;

            receptionScheduleRef.current?.open(
                buttonId,
                () => void 0,
                organizationId,
            );
        };

        return () => {
            delete host.openReceptionSchedule;
        };
    }, []);

    const notifyReceptionBookingChanged = useCallback(() => {
        if (!service?.id) return;

        refDiv.current?.dispatchEvent(
            new CustomEvent('onReceptionBookingChanged', {
                detail: { serviceId: service.id },
                bubbles: true,
                composed: true,
            }),
        );
    }, [service?.id]);

    if (!service?.id) return null;

    return (
        <div ref={refDiv}>
            <ReceptionSchedulesSection
                serviceId={service.id}
                onBookingChanged={notifyReceptionBookingChanged}
                hideTimer={hideTimer}
            />
            <ReceptionScheduleSlotModal
                key={service.id}
                ref={receptionScheduleRef}
                serviceId={service.id}
                onBookingChanged={notifyReceptionBookingChanged}
            />
        </div>
    );
};
